import { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDispatch } from 'react-redux';
import { getAuthCookieInfo } from 'utils/functions/authentication';
import { createDiscount } from '../../actions';
import type { DiscountProduct, OfferLineDraft } from '../../types/discount.types';
import discountsStrings from '../../strings';
import { buildCreateOfferPayload, productSelectionKey } from '../../utils/discountHelpers';

const today = new Date().toISOString().slice(0, 10);

const lineSchema = z.object({
  productKey: z.string(),
  productShapeId: z.number(),
  originalPrice: z.number(),
  discountValue: z.string().min(1, discountsStrings.validationRequired),
  discountType: z.enum(['percent', 'amount']),
}).superRefine((data, ctx) => {
  const value = Number(data.discountValue);
  if (value <= 0) {
    ctx.addIssue({ code: 'custom', message: discountsStrings.validationNegativeDiscount, path: ['discountValue'] });
  }
  if (data.discountType === 'percent' && value > 100) {
    ctx.addIssue({ code: 'custom', message: discountsStrings.validationPercentMax, path: ['discountValue'] });
  }
  if (data.discountType === 'amount' && value > data.originalPrice) {
    ctx.addIssue({ code: 'custom', message: discountsStrings.validationAmountExceedsPrice, path: ['discountValue'] });
  }
});

const formSchema = z.object({
  startDate: z.string().min(1, discountsStrings.validationRequired),
  endDate: z.string().min(1, discountsStrings.validationRequired),
  lines: z.array(lineSchema),
}).superRefine((data, ctx) => {
  if (data.startDate && data.startDate < today) {
    ctx.addIssue({ code: 'custom', message: discountsStrings.validationStartDateBeforeToday, path: ['startDate'] });
  }
  if (data.startDate && data.endDate && data.endDate <= data.startDate) {
    ctx.addIssue({ code: 'custom', message: discountsStrings.validationEndDateAfterStart, path: ['endDate'] });
  }
});

export type CreateDiscountFormValues = z.infer<typeof formSchema>;

function offerLinesToProducts(lines: OfferLineDraft[]): DiscountProduct[] {
  return lines.map(line => ({
    productKey: line.productKey,
    productShapeId: line.productShapeId,
    productName: line.productName,
    productShapeName: line.productShapeName,
    productImageUrl: line.productImageUrl,
    originalPrice: line.originalPrice,
    existingOfferStatus: line.existingOfferStatus,
    existingOfferStatusLabel: line.existingOfferStatusLabel,
  }));
}

function applyProductsToLines(
  selectedProducts: DiscountProduct[],
  existingLines: OfferLineDraft[],
): OfferLineDraft[] {
  const existingByKey = new Map(
    existingLines.map(line => [productSelectionKey(line.productKey, line.productShapeId), line]),
  );

  return selectedProducts.map(product => {
    const key = productSelectionKey(product.productKey, product.productShapeId);
    const existing = existingByKey.get(key);
    if (existing) return existing;

    return {
      productKey: product.productKey,
      productShapeId: product.productShapeId,
      productName: product.productName,
      productShapeName: product.productShapeName,
      productImageUrl: product.productImageUrl,
      originalPrice: product.originalPrice,
      existingOfferStatus: product.existingOfferStatus,
      existingOfferStatusLabel: product.existingOfferStatusLabel,
      discountValue: '',
      discountType: 'percent',
    };
  });
}

interface UseCreateDiscountFormOptions {
  isOpen: boolean;
}

export function useCreateDiscountForm({ isOpen }: UseCreateDiscountFormOptions) {
  const dispatch = useDispatch();
  const [offerLines, setOfferLines] = useState<OfferLineDraft[]>([]);
  const [draftProducts, setDraftProducts] = useState<DiscountProduct[]>([]);
  const [isSelectProductsOpen, setIsSelectProductsOpen] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<CreateDiscountFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      startDate: '',
      endDate: '',
      lines: [],
    },
  });

  const { fields } = useFieldArray({ control, name: 'lines' });
  const watchedStartDate = watch('startDate');

  useEffect(() => {
    if (isOpen) return;
    setOfferLines([]);
    setDraftProducts([]);
    setIsSelectProductsOpen(false);
    reset({ startDate: '', endDate: '', lines: [] });
  }, [isOpen, reset]);

  useEffect(() => {
    if (!isOpen) return;
    reset(current => ({
      startDate: current.startDate,
      endDate: current.endDate,
      lines: offerLines.map(line => ({
        productKey: line.productKey,
        productShapeId: line.productShapeId,
        originalPrice: line.originalPrice ?? 0,
        discountValue: line.discountValue,
        discountType: line.discountType,
      })),
    }));
  }, [offerLines, isOpen, reset]);

  const handleOpenSelectProducts = () => {
    setDraftProducts(offerLinesToProducts(offerLines));
    setIsSelectProductsOpen(true);
  };

  const handleToggleDraftProduct = (product: DiscountProduct) => {
    const key = productSelectionKey(product.productKey, product.productShapeId);
    setDraftProducts(prev => {
      const exists = prev.some(p => productSelectionKey(p.productKey, p.productShapeId) === key);
      return exists
        ? prev.filter(p => productSelectionKey(p.productKey, p.productShapeId) !== key)
        : [...prev, product];
    });
  };

  const handleCloseSelectProducts = () => {
    setIsSelectProductsOpen(false);
  };

  const handleApplySelectedProducts = () => {
    setOfferLines(prev => applyProductsToLines(draftProducts, prev));
    setIsSelectProductsOpen(false);
  };

  const handleRemoveLine = (productKey: string, productShapeId: number) => {
    setOfferLines(prev =>
      prev.filter(
        line => !(line.productKey === productKey && line.productShapeId === productShapeId),
      ),
    );
  };

  const handleFormSubmit = handleSubmit(data => {
    const { pharmacyKey } = getAuthCookieInfo();
    dispatch(
      createDiscount(
        buildCreateOfferPayload({
          offerLines: data.lines.map(line => ({
            ...line,
            productName: '',
            productShapeName: '',
            productImageUrl: '',
            originalPrice: line.originalPrice,
            existingOfferStatus: null,
            existingOfferStatusLabel: null,
          })),
          startDate: data.startDate,
          endDate: data.endDate,
          providerKey: pharmacyKey,
        }),
      ),
    );
  });

  return {
    register,
    errors,
    fields,
    watchedStartDate,
    offerLines,
    draftProducts,
    isSelectProductsOpen,
    handleOpenSelectProducts,
    handleToggleDraftProduct,
    handleCloseSelectProducts,
    handleApplySelectedProducts,
    handleRemoveLine,
    handleFormSubmit,
  };
}
